import { deepClone } from '../utils/object';
import { baseName } from '../utils/string';

import {
	CHANGE_DEVICE,
	COOKIE_CONSENT,
	CONSUMER_TYPE,
	RESHOW_COOKIE_NOTIFICATION,
	CHANGE_STOCKIST_COUNTRY,
	CHANGE_STOCKIST_STATE,
	CHANGE_STOCKIST_TYPE,
	INIT,
	SITE_INIT,
	OPEN_MENU,
	OPEN_CART,
	SET_QUERY,
	CHANGE_LANGUAGE,
	CHANGE_COUNTRY,
	UPDATE_EVENT_DETAILS,
	UPDATE_EVENT_DETAILS_ERRORS,
	PARTICIPANTS_REGISTERED,
	PARTICIPANTS_ERROR,
	ACUITY_IFRAME_HEIGHT,
} from '../actions/environment';
import { setCookie, getCookie, deleteCookie, addClass, removeClass } from '../utils/dom';

const initialState = {
	properties: {
		openCart: false,
		siteInitiated: false,
		siteUpdated: false,
	},
	cookieConsent: ['default'],
	reshowCookieNotification: false,
	stockistCountry: 'US',
	stockistState: '',
	stockistType: '',
	downloads: {},
	setup: {},
	header: {},
	footer: {},
	searchInputText: '',
	hashLocation: '',
	hostname: '',
	navigation: {
		open: false,
	},
	loadedCategories: [],
	acuityIframeHeight: 1100,
	pagination: {},
	favourites: [],
	eventDetails: {
		allowFriends: false,
		subscribeToNewsletter: false,
		acceptTerms: false,
		serverErrors: [],
		errors: {},
	},
};

if (typeof document !== 'undefined') initialState.cookieConsent = getCookie('cookies', true);

const environment = (state = initialState, action) => {
	switch (action.type) {
		case INIT:
			return {
				...state,
				properties: {
					...state.properties,
					siteClosed: action.initData.siteClosed || false,
					siteInitiated: true,
				},
				setup: {
					...state.setup,
					...action.initData.setup,
				},
				header: {
					...state.header,
					...action.initData.header,
				},
				magazineSnippets: action.initData.magazineSnippets,
				footer: {
					...state.footer,
					...action.initData.footer,
				},
				navigation: {
					...state.navigation,
					...action.initData.navigation,
				},
			};

		case CHANGE_DEVICE:
			return {
				...state,
				setup: {
					...state.setup,
					breakPoint: action.breakPoint,
				},
			};

		case COOKIE_CONSENT:
			return {
				...state,
				// null means no decision has been made yet (see getCookie('cookies') below/in
				// routesMap.js), distinct from an explicit-but-empty/declined-all choice.
				cookieConsent: action.consent ? action.consent.slice() : null,
			};

		case CONSUMER_TYPE:
			return {
				...state,
				setup: {
					...state.setup,
					consumerType: action.setType,
				},
			};

		case RESHOW_COOKIE_NOTIFICATION:
			deleteCookie('cookies');

			return {
				...state,
				cookieConsent: null,
				reshowCookieNotification: !state.reshowCookieNotification,
			};

		case CHANGE_STOCKIST_COUNTRY:
			return {
				...state,
				stockistCountry: action.setCountry,
			};

		case CHANGE_STOCKIST_STATE:
			return {
				...state,
				stockistState: action.setState,
			};

		case CHANGE_STOCKIST_TYPE:
			return {
				...state,
				stockistType: action.setType,
			};

		case ACUITY_IFRAME_HEIGHT:
			return {
				...state,
				acuityIframeHeight: action.height,
			};

		case 'DOWNLOADS': {
			const nextDownloads = {
				...state.downloads,
				...deepClone(action.downloads),
			};

			const flatten = (d, lookup = {}) => {
				const keys = Object.keys(d);

				for (let i = 0; i < keys.length; i += 1) {
					if (d[keys[i]].files) {
						for (let j = 0; j < d[keys[i]].files.length; j += 1) {
							const key = baseName(d[keys[i]].files[j].link, '/', true);
							lookup[key] = d[keys[i]].files[j].link;
						}
					}
					if (d[keys[i]].children) flatten(d[keys[i]].children, lookup);
				}

				return lookup;
			};

			const downloadLookup = flatten(nextDownloads);

			return {
				...state,
				downloads: nextDownloads,
				setup: {
					...state.setup,
					downloadLookup,
				},
			};
		}

		case 'COUNTRIES':
			return {
				...state,
				setup: {
					...state.setup,
					countryData: action.countries,
				},
			};

		case 'SITE_UPDATED':
			return {
				...state,
				properties: {
					...state.properties,
					siteUpdated: true,
				},
			};

		case 'UPDATE_PAGINATION':
			return {
				...state,
				pagination: {
					...state.pagination,
					[action.currentPath]: action.currentPage,
				},
			};

		case 'APPEND_CATEGORY':
			return {
				...state,
				loadedCategories: Array.isArray(action.category) ? [...action.category] : [...state.loadedCategories, action.category],
			};

		case 'UPDATE_HASH':
			return {
				...state,
				hashLocation: action.currentHash || action.hash,
			};

		case SITE_INIT:
			return {
				...state,
				properties: {
					...state.properties,
					siteInitiated: true,
				},
			};

		case CHANGE_LANGUAGE: {
			if (typeof document !== 'undefined') {
				// Is set on server unconditionally so only valid for client
				setCookie('settings', [action.language, state.setup.country, parseInt(state.setup.market, 10), parseInt(state.setup.pricelist, 10)]);
			}
			return {
				...state,
				setup: {
					...state.setup,
					language: action.language,
				},
			};
		}

		case CHANGE_COUNTRY: {
			if (typeof document !== 'undefined') {
				// Is set on server unconditionally so only valid for client
				setCookie('settings', [state.setup.language, action.country, parseInt(action.market, 10), parseInt(action.pricelist, 10)]);
			}
			return {
				...state,
				setup: {
					...state.setup,
					country: action.country,
					market: action.market,
					pricelist: action.pricelist,
					currency: action.currency,
				},
			};
		}

		case OPEN_MENU: {
			const open = action.open !== undefined ? action.open : !state.navigation.open;
			if (typeof document !== 'undefined') {
				// Is set on server unconditionally so only valid for client
				if (open) addClass(document.body, 'u_menuIsOpen');
				else removeClass(document.body, 'u_menuIsOpen');
			}
			return {
				...state,
				navigation: {
					...state.navigation,
					open,
				},
			};
		}

		case OPEN_CART:
			return {
				...state,
				properties: {
					...state.properties,
					openCart: action.open !== undefined ? action.open : !state.properties.openCart,
				},
			};

		case SET_QUERY:
			return {
				...state,
				setup: {
					...state.setup,
					[action.key]: action.value,
				},
			};

		case 'SEARCH':
			return {
				...state,
				searchInputText: action.text,
			};

		case 'TOGGLE_FAVOURITE': {
			let favourites = [...state.favourites];
			const index = favourites.indexOf(action.productId);

			if (index === -1) {
				favourites = [...favourites, action.productId];
			} else {
				favourites = [...favourites.slice(0, index), ...favourites.slice(index + 1)];
			}

			localStorage.setItem('favourites', JSON.stringify(favourites));

			return {
				...state,
				favourites,
			};
		}

		case 'UPDATE_FAVOURITES':
			return {
				...state,
				favourites: [...action.favourites],
			};

		case UPDATE_EVENT_DETAILS:
			return {
				...state,
				eventDetails: {
					...state.eventDetails,
					[action.id]: action.value ? action.value : !state.eventDetails[action.id],
					eventRegistrationFailed: false,
					eventRegistrationSucceded: false,
					serverErrors: [],
				},
			};

		case UPDATE_EVENT_DETAILS_ERRORS:
			return {
				...state,
				eventDetails: {
					...state.eventDetails,
					errors: {
						...state.eventDetails.errors,
						[action.id]: action.error,
					},
				},
			};

		case PARTICIPANTS_REGISTERED:
			return {
				...state,
				eventDetails: {
					...initialState.eventDetails,
					eventRegistrationSucceded: true,
					eventRegistrationFailed: false,
					serverErrors: [],
				},
			};

		case PARTICIPANTS_ERROR:
			return {
				...state,
				eventDetails: {
					...state.eventDetails,
					eventRegistrationFailed: true,
					eventRegistrationSucceded: false,
					serverErrors: action.errors ? [...action.errors] : [],
				},
			};

		default:
			return state;
	}
};

export { initialState as environmentState };
export default environment;
